import { is } from '../util/is';
import { reduce } from '../collections/reduce';

/**
 * A data structure that can be mapped over.
 */
type Functor<T> = T[] | Record<string, T>;

type MapCallback<A, B> = (a: A, keyOrIndex?: number | string) => B;

/**
 * Takes a function of type `A -> B` and a collection of type `F<A>` and returns a collection of type `F<B>`.
 *
 * ```ts
 * tt.map(c => String.fromCharCode(c), [[65, 66, 67]]);
 * // => ['A', 'B', 'C']
 * ```
 *
 * @param fn The function that transforms A -> B; can get index/key as second argument.
 * @param f The functor to be mapped over.
 */
export function map<A, B>(fn: MapCallback<A, B>, f: A[]): B[];
export function map<A, B>(fn: MapCallback<A, B>, f: Record<string, A>): Record<string, B>;
export function map<A, B>(fn: MapCallback<A, B>, f: Functor<A>): Functor<B> {
  if (is(Array, f)) {
    return reduce<B[], A>(
      (acc, x, i) => {
        acc.push(fn(x, i));
        return acc;
      },
      [],
      f
    );
  } else {
    return reduce<Record<string, B>, A>(
      (acc, x, k) => {
        acc[k] = fn(x, k);
        return acc;
      },
      {},
      f
    );
  }
}
